/**
 * PURPOSE: Mostrar un Comunicado PUBLICADO en modo sólo lectura.
 * RESPONSIBILITIES: Nunca renderizar controles de edición para contenido publicado (FTR-0005 §22 —
 *   el backend rechaza esos intentos con 409; la UI evita el estado inválido en vez de dejar que
 *   ocurra), mostrar estado de push con la terminología aprobada y ofrecer sólo "Ocultar".
 * COLLABORATORS: HayllisCommunicationsPanel.jsx, hayllisCommunicationsDisplay.utils.js.
 * ROUTE: winscore/src/admin/HayllisCommunications/HayllisCommunicationDetailReadOnly.jsx
 */
import React from "react";
import { FiFile, FiImage } from "react-icons/fi";

import {
  ATTACHMENT_TYPE_LABELS,
  buildPushSummary,
  formatBytes,
  formatDateTime,
  PUSH_STATUS_LABELS,
} from "./hayllisCommunicationsDisplay.utils.js";

const PUSH_STATUS_TONE = {
  NOT_SENT: "bg-slate-100 text-slate-600",
  SENDING: "bg-blue-100 text-blue-700",
  SENT: "bg-emerald-100 text-emerald-700",
  PARTIAL: "bg-amber-100 text-amber-800",
  FAILED: "bg-red-100 text-red-700",
};

const HayllisCommunicationDetailReadOnly = ({ notification, onRequestHide }) => {
  if (!notification) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
          Publicado
        </span>
        {!notification.visible && (
          <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-600">
            Oculto (no visible en Hayllis Mobile)
          </span>
        )}
        <span
          className={`rounded-full px-3 py-1 text-xs font-bold ${PUSH_STATUS_TONE[notification.pushStatus] || "bg-slate-100 text-slate-600"}`}
        >
          {PUSH_STATUS_LABELS[notification.pushStatus] || notification.pushStatus}
        </span>
      </div>

      <div>
        <h3 className="text-lg font-bold text-slate-800 dark:text-white">{notification.title}</h3>
        <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-200">{notification.body}</p>
      </div>

      {notification.attachment ? (
        <a
          href={notification.attachment.url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-2 rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm hover:bg-slate-100 dark:border-slate-600 dark:bg-gray-800"
        >
          {notification.attachment.mime === "application/pdf" ? (
            <FiFile className="shrink-0 text-red-500" />
          ) : (
            <FiImage className="shrink-0 text-blue-500" />
          )}
          <span className="min-w-0 flex-1 truncate font-semibold text-slate-800 dark:text-white">
            {notification.attachment.name}
          </span>
          <span className="shrink-0 text-xs font-normal text-slate-500 dark:text-slate-400">
            {ATTACHMENT_TYPE_LABELS[notification.attachment.mime] || notification.attachment.mime}
            {formatBytes(notification.attachment.size) ? ` · ${formatBytes(notification.attachment.size)}` : ""}
          </span>
        </a>
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400">Sin adjunto.</p>
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm dark:border-slate-600 dark:bg-gray-800">
        <span className="font-semibold text-slate-600 dark:text-slate-300">Audiencia: </span>
        <span className="text-slate-700 dark:text-white">Todos</span>
      </div>

      <div className="rounded-lg border border-slate-200 p-3 text-sm dark:border-slate-600">
        <p className="font-semibold text-slate-700 dark:text-slate-200">{buildPushSummary(notification)}</p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Estos contadores reflejan aceptación por Expo, no confirmación de entrega ni de lectura por el usuario.
        </p>
      </div>

      <dl className="grid grid-cols-1 gap-2 text-xs text-slate-500 dark:text-slate-400 sm:grid-cols-2">
        <div>
          <dt className="font-semibold uppercase tracking-wide">Creado</dt>
          <dd>{formatDateTime(notification.createdAt)} · {notification.createdBy?.username || "—"}</dd>
        </div>
        <div>
          <dt className="font-semibold uppercase tracking-wide">Publicado</dt>
          <dd>{formatDateTime(notification.publishedAt)} · {notification.publishedBy?.username || "—"}</dd>
        </div>
      </dl>

      {notification.visible && (
        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={() => onRequestHide(notification)}
            className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
          >
            Ocultar
          </button>
        </div>
      )}
    </div>
  );
};

export default HayllisCommunicationDetailReadOnly;
